import React, { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface TruncatedTextProps {
  /** Full text. Also used as the tooltip content. */
  text: string;
  /** Classes applied to the visible (truncated) element. */
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  /** Max width of the tooltip bubble in px. */
  maxWidth?: number;
}

interface TooltipPos {
  top: number;
  left: number;
  placement: "top" | "bottom";
}

const GAP = 8;

export const TruncatedText = ({
  text,
  className = "",
  onClick,
  maxWidth = 360,
}: TruncatedTextProps) => {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [pos, setPos] = useState<TooltipPos | null>(null);

  const show = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Only bother if the content is genuinely overflowing.
    if (el.scrollWidth <= el.clientWidth + 1) return;

    const rect = el.getBoundingClientRect();
    const placement: "top" | "bottom" = rect.top < 120 ? "bottom" : "top";
    const half = Math.min(maxWidth, window.innerWidth - 16) / 2;
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, half + 8),
      window.innerWidth - half - 8,
    );

    setPos({
      top: placement === "top" ? rect.top - GAP : rect.bottom + GAP,
      left,
      placement,
    });
  }, [maxWidth]);

  const hide = useCallback(() => setPos(null), []);

  return (
    <>
      <span
        ref={ref}
        className={`block truncate ${className}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onClick={onClick}
      >
        {text}
      </span>

      {pos &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              maxWidth,
              transform:
                pos.placement === "top"
                  ? "translate(-50%, -100%)"
                  : "translate(-50%, 0)",
              zIndex: 9999,
              pointerEvents: "none",
            }}
            className="rounded-md  bg-emphasis px-2.5 py-1.5 text-sm leading-snug text-neutral-100 shadow-xl whitespace-pre-wrap break-words"
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
};

export default TruncatedText;